import { useCallback } from 'react';
import { useActorRef, useSelector } from '@xstate5/react';

import { remoteAssetMachine } from './machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook to download a remote asset and store it locally.
 * Useful for caching assets videos.
 */
export const useRemoteAsset = (params: Params) => {
  const { url } = params;

  const actorRef = useActorRef(remoteAssetMachine, { input: { url } });

  // ─── States ──────────────────────────────────────────────────────────

  const isDownloading = useSelector(actorRef, (state) => state.matches('downloading')); // prettier-ignore
  const hasDownloaded = useSelector(actorRef, (state) => state.matches('downloaded')); // prettier-ignore
  const failedToDownload = useSelector(actorRef, (state) => state.matches('failure')); // prettier-ignore

  // ─── Derived Data ────────────────────────────────────────────────────

  const localURI = useSelector(actorRef, (state) => state.context.localURI);
  const error = useSelector(actorRef, (state) => state.context.error);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const retry = useCallback(() => {
    actorRef.send({ type: 'retry' });
  }, [actorRef]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isDownloading,
    hasDownloaded,
    failedToDownload,
    localURI,
    error,
    retry,
  };
};

// ─── Type ────────────────────────────────────────────────────────────────────

type Params = {
  url: string;
};
